import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { getToken } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { subscribeForPush } from '../utils/push';
import BrandLogo from './BrandLogo';
import ProfileModal from './ProfileModal';
import { AnimatedNumber } from '../motion';
import { LayoutGroup, motion } from 'motion/react';
import usePrefersReducedMotion from '../motion/usePrefersReducedMotion';
import { SPRING_PILL } from '../motion/tokens';
import DropdownPanel from './ui/DropdownPanel';
import NotificationDetailModal from './NotificationDetailModal';
import NotificationsHistoryModal from './NotificationsHistoryModal';
import NotificationStackRow from './NotificationStackRow';
import { isAppointmentsRelatedPush, isZoomPushType as isZoomPushHelper } from '../utils/appointmentHelpers';
import {
  loadPushNotificationHistory,
  savePushNotificationHistory,
  mergePushNotification,
  markPushNotificationsRead,
  unreadPushCount,
  groupPushNotifications,
  PUSH_HISTORY_PREVIEW_COUNT,
} from '../utils/pushNotificationHistory';
import {
  Menu,
  X,
  Bell,
  Video,
  Mail,
  AlertTriangle,
  Heart,
  UserRound,
  Lock,
  LogOut,
} from 'lucide-react';

const NAV_LINKS = [
  { to: '/mydashboard', label: 'Dashboard', end: true },
  { to: '/my-patients', label: 'Patients' },
  { to: '/alerts', label: 'Alerts', badge: 'alerts' },
  { to: '/appointments', label: 'Appointments' },
  { to: '/medications', label: 'Medications' },
  { to: '/messages', label: 'Messages', badge: 'messages' },
];

const navLinkClass = ({ isActive }) =>
  [
    'relative inline-flex items-center gap-1.5 py-2 font-medium text-sm outline-none rounded-md transition-colors duration-150',
    'focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',
    isActive ? 'text-teal-700' : 'text-gray-600 hover:text-teal-600',
  ].join(' ');

const mobileNavLinkClass = ({ isActive }) =>
  [
    'flex items-center justify-between gap-3 py-4 px-4 rounded-lg font-medium text-base outline-none min-h-[48px] transition-colors',
    'focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2',
    isActive
      ? 'text-teal-700 bg-teal-50'
      : 'text-gray-800 hover:text-teal-600 hover:bg-gray-50',
  ].join(' ');

const isZoomPushType = (item) => isZoomPushHelper(item);

const isChatPushType = (type) => {
  const t = String(type || '').toLowerCase();
  return t === 'chat_message';
};

const Header = () => {
  const navigate = useNavigate();
  const { user, userType, logout, isAuthenticated } = useAuth();
  const loggedIn = isAuthenticated === true;
  const onPortal =
    typeof window !== 'undefined' && window.location.hostname.startsWith('portal.');
  // Absolute marketing URL when logged out — avoids SPA "/" → LoginPage on apex content pages.
  const logoTo = loggedIn
    ? (onPortal ? '/mydashboard' : 'https://portal.vitra-health.com/mydashboard')
    : 'https://vitra-health.com/';
  const { unreadMessagesCount, unreadByConversation } = useNotification();
  const [userInfo, setUserInfo] = useState({ fullName: '', role: '' });
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const [feelingsSummary, setFeelingsSummary] = useState({ unwell: 0, moderate: 0 });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [webPushBellCount, setWebPushBellCount] = useState(0);
  const [webPushItems, setWebPushItems] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState('unknown');
  const [detailItem, setDetailItem] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const historyHydratedRef = useRef(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const reducedMotion = usePrefersReducedMotion();

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const refreshNotificationPermission = useCallback(() => {
    if (typeof Notification === 'undefined') {
      setNotificationPermission('unsupported');
      return;
    }
    setNotificationPermission(Notification.permission || 'default');
  }, []);

  useEffect(() => {
    refreshNotificationPermission();
  }, [refreshNotificationPermission]);

  useEffect(() => {
    const onVisibility = () => refreshNotificationPermission();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refreshNotificationPermission]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user?.user_id) return;

      try {
        const token = getToken();
        if (!token) return;

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        if (userType === 'clinician') {
          const response = await fetch(`${API_BASE_URL}/clinicians/user/${user.user_id}`, { headers });
          if (response.ok) {
            const clinicianData = await response.json();
            const fullName = `${clinicianData.firstName || ''} ${clinicianData.lastName || ''}`.trim();
            setUserInfo({
              fullName: fullName || 'User',
              role: clinicianData.role || 'Clinician'
            });
          }
        } else if (userType === 'admin') {
          try {
            const response = await fetch(`${API_BASE_URL}/admins/user/${user.user_id}`, { headers });
            if (response.ok) {
              const adminData = await response.json();
              const fullName = `${adminData.firstName || ''} ${adminData.lastName || ''}`.trim();
              setUserInfo({
                fullName: fullName || 'Admin',
                role: 'Administrator'
              });
            } else {
              setUserInfo({
                fullName: 'Admin User',
                role: 'Administrator'
              });
            }
          } catch (error) {
            setUserInfo({
              fullName: 'Admin User',
              role: 'Administrator'
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUserInfo();
  }, [user, userType, API_BASE_URL]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && mobileMenuButtonRef.current && !mobileMenuButtonRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationOpen || isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen, isNotificationOpen, isMobileMenuOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
      };
    }
  }, [isMobileMenuOpen]);

  const fetchAlertsCount = useCallback(async () => {
    const token = getToken();
    if (!token || !API_BASE_URL) return;
    if (userType !== 'clinician' && userType !== 'admin') return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE_URL}/alerts/active-count`, { headers });
      const data = res.ok ? await res.json() : {};
      const n = Number(data?.count);
      setAlertsCount(Number.isFinite(n) && n > 0 ? Math.min(999, Math.floor(n)) : 0);
    } catch (err) {
      console.error('Error fetching alerts count:', err);
    }
  }, [API_BASE_URL, userType]);

  const fetchNotificationCounts = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setNotificationLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (userType === 'clinician') {
        const [patientsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/patients/`, { headers }),
          fetchAlertsCount(),
        ]);
        const patientsData = patientsRes.ok ? await patientsRes.json() : [];
        const mrns = (patientsData || []).map((p) => p.MRN).filter(Boolean);
        if (mrns.length === 0) {
          setFeelingsSummary({ unwell: 0, moderate: 0 });
        } else {
          const feelingPromises = mrns.slice(0, 50).map((mrn) =>
            fetch(`${API_BASE_URL}/patient-daily-feelings/patient/${mrn}/latest`, { headers })
              .then((r) => (r.ok ? r.json() : null))
              .then((d) => d?.feelingTag?.toLowerCase())
              .catch(() => null)
          );
          const feelings = await Promise.all(feelingPromises);
          setFeelingsSummary({
            unwell: feelings.filter((f) => f === 'unwell').length,
            moderate: feelings.filter((f) => f === 'moderate').length,
          });
        }
      } else if (userType === 'admin') {
        await fetchAlertsCount();
      }
    } catch (err) {
      console.error('Error fetching notification counts:', err);
    } finally {
      setNotificationLoading(false);
    }
  }, [API_BASE_URL, userType, fetchAlertsCount]);

  useEffect(() => {
    if (isNotificationOpen) fetchNotificationCounts();
  }, [isNotificationOpen, fetchNotificationCounts]);

  useEffect(() => {
    if (!user?.user_id || (userType !== 'clinician' && userType !== 'admin')) return;
    fetchAlertsCount();
    const t = window.setInterval(fetchAlertsCount, 45_000);
    return () => window.clearInterval(t);
  }, [user?.user_id, userType, fetchAlertsCount]);

  useEffect(() => {
    const onAlertsChanged = () => fetchAlertsCount();
    window.addEventListener('vitra-alerts-changed', onAlertsChanged);
    return () => window.removeEventListener('vitra-alerts-changed', onAlertsChanged);
  }, [fetchAlertsCount]);

  useEffect(() => {
    historyHydratedRef.current = false;
    const loaded = loadPushNotificationHistory(user?.user_id);
    setWebPushItems(loaded);
    setWebPushBellCount(unreadPushCount(loaded));
    historyHydratedRef.current = true;
  }, [user?.user_id]);

  useEffect(() => {
    if (!historyHydratedRef.current) return;
    savePushNotificationHistory(user?.user_id, webPushItems);
    setWebPushBellCount(unreadPushCount(webPushItems));
  }, [webPushItems, user?.user_id]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onSwMessage = (event) => {
      if (event?.data?.type !== 'WEB_PUSH_RECEIVED') return;
      const payload = event?.data?.payload || {};
      const item = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: payload.title || 'New notification',
        body: payload.body || '',
        notificationType: payload.notificationType || '',
        url: payload.url || '/messages',
        sessionName: payload.sessionName || payload.session_name || '',
        appointmentId: payload.appointmentId || payload.appointment_id || null,
        conversationId: payload.conversationId || payload.conversation_id || null,
        messageId: payload.messageId || payload.message_id || null,
        patientMrn: payload.patientMrn || payload.patient_mrn || null,
        receivedAt: new Date().toISOString(),
        read: false,
      };
      setWebPushItems((prev) =>
        mergePushNotification(prev, item, { isZoomType: isZoomPushType })
      );
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleEnableNotifications = async () => {
    if (typeof Notification === 'undefined') {
      setNotificationPermission('unsupported');
      return;
    }
    try {
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      refreshNotificationPermission();
      if (API_BASE_URL) {
        await subscribeForPush(API_BASE_URL, getToken);
      }
    } catch {}
  };

  const markNotificationRead = useCallback((id) => {
    if (!id) return;
    setWebPushItems((prev) => markPushNotificationsRead(prev, id));
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setWebPushItems((prev) => markPushNotificationsRead(prev));
  }, []);

  const notificationStacks = useMemo(
    () => groupPushNotifications(webPushItems),
    [webPushItems],
  );

  const resolveStack = useCallback(
    (selection) => {
      if (!selection) return null;
      if (selection.latest && Array.isArray(selection.items)) return selection;
      return (
        notificationStacks.find((stack) =>
          stack.items.some((entry) => String(entry.id) === String(selection.id)),
        ) || {
          key: selection.id,
          kind: 'single',
          items: [selection],
          latest: selection,
          count: 1,
          unreadCount: selection.read ? 0 : 1,
          read: Boolean(selection.read),
          receivedAt: selection.receivedAt,
          title: selection.title || 'Notification',
          subtitle: selection.body || '',
          typeLabel: 'Notification',
        }
      );
    },
    [notificationStacks],
  );

  const handleViewNotificationDetail = (selection) => {
    const stack = resolveStack(selection);
    if (!stack?.latest) return;
    const ids = stack.items.map((entry) => entry.id);
    setWebPushItems((prev) => markPushNotificationsRead(prev, ids));
    setDetailItem(stack);
    setIsNotificationOpen(false);
    setIsHistoryOpen(false);
  };

  const handleOpenNotificationDestination = (item) => {
    const stack = resolveStack(item);
    if (stack?.items?.length) {
      setWebPushItems((prev) =>
        markPushNotificationsRead(
          prev,
          stack.items.map((entry) => entry.id),
        ),
      );
    } else {
      markNotificationRead(item?.id);
    }
    setDetailItem(null);
    setIsNotificationOpen(false);
    setIsHistoryOpen(false);
    const url = stack?.latest?.url || item?.url;
    if (url) navigate(url);
  };

  const handleCloseNotificationDetail = () => {
    setDetailItem(null);
  };

  const handleOpenHistory = () => {
    setIsNotificationOpen(false);
    setIsHistoryOpen(true);
  };

  const handleNotificationBellClick = async () => {
    setIsNotificationOpen((prev) => !prev);
    if (!API_BASE_URL) return;
    try {
      await subscribeForPush(API_BASE_URL, getToken);
    } catch {}
  };

  const appointmentPushItems = webPushItems.filter((item) =>
    isAppointmentsRelatedPush(item)
  );
  const chatPushItems = webPushItems.filter((item) => isChatPushType(item.notificationType));
  const appointmentPushCount = appointmentPushItems.filter((item) => !item.read).length;
  const totalMessageCount = unreadMessagesCount;
  const totalAppointmentCount = appointmentPushCount;
  const latestAppointmentPush = appointmentPushItems[0];
  const recentStackPreview = notificationStacks.slice(0, PUSH_HISTORY_PREVIEW_COUNT);
  const hasMoreNotifications = notificationStacks.length > PUSH_HISTORY_PREVIEW_COUNT;
  const bellTotal = [
    unreadMessagesCount,
    alertsCount,
    feelingsSummary.unwell,
    feelingsSummary.moderate,
    webPushBellCount,
  ].reduce((a, b) => a + b, 0);

  return (
    <>
      <header
        className="sticky top-0 z-header w-full bg-white shadow-sm border-b border-gray-200"
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 min-h-[4rem]">
            <div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
              <BrandLogo to={logoTo} />

              {loggedIn && (
              <nav
                className="hidden lg:flex items-center gap-6 xl:gap-8"
                aria-label="Main navigation"
              >
                <LayoutGroup id="vitra-main-nav">
                  {NAV_LINKS.map(({ to, label, badge, end }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={!!end}
                      className={navLinkClass}
                    >
                      {({ isActive }) => (
                        <>
                          <span>{label}</span>
                          {badge === 'messages' && totalMessageCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold text-white bg-red-500 shadow-sm" aria-hidden="true">
                              <AnimatedNumber
                                value={totalMessageCount > 99 ? '99+' : totalMessageCount}
                              />
                            </span>
                          )}
                          {label === 'Appointments' && totalAppointmentCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold text-white bg-red-500 shadow-sm" aria-hidden="true">
                              <AnimatedNumber
                                value={totalAppointmentCount > 99 ? '99+' : totalAppointmentCount}
                              />
                            </span>
                          )}
                          {badge === 'alerts' && alertsCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold text-white bg-red-500 shadow-sm" aria-hidden="true">
                              <AnimatedNumber
                                value={alertsCount > 99 ? '99+' : alertsCount}
                              />
                            </span>
                          )}
                          {isActive && !reducedMotion && (
                            <motion.span
                              layoutId="vitra-main-nav-underline"
                              className="absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-teal-600"
                              transition={SPRING_PILL}
                            />
                          )}
                          {isActive && reducedMotion && (
                            <span className="absolute left-0 right-0 -bottom-0.5 h-0.5 rounded-full bg-teal-600" />
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </LayoutGroup>
              </nav>
              )}
            </div>

            {loggedIn && (
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="lg:hidden">
                <button
                  type="button"
                  ref={mobileMenuButtonRef}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-teal-600 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-nav-menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <Menu className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
                  )}
                </button>
              </div>

              <div className="relative" ref={notificationRef}>
                <button
                  type="button"
                  onClick={handleNotificationBellClick}
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 hover:text-teal-600 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Notifications"
                  aria-expanded={isNotificationOpen}
                >
                  <Bell className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
                  {(bellTotal > 0) && (
                    <span className="absolute top-1 right-1 flex h-4 min-w-[1rem] px-0.5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                      <AnimatedNumber value={bellTotal > 99 ? '99+' : bellTotal} />
                    </span>
                  )}
                </button>
                <DropdownPanel
                  open={isNotificationOpen}
                  className="absolute right-0 mt-2 w-[min(20.5rem,calc(100vw-1.25rem))] bg-white rounded-xl shadow-xl border border-gray-200/90 z-dropdown overflow-hidden"
                >
                    <div className="px-3.5 py-2.5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                      <div className="flex items-center gap-2">
                        {webPushBellCount > 0 && (
                          <button
                            type="button"
                            onClick={markAllNotificationsRead}
                            className="text-[11px] font-medium text-gray-500 hover:text-teal-700"
                          >
                            Mark seen
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleOpenHistory}
                          className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-teal-700 transition-colors"
                        >
                          View all
                          {notificationStacks.length > 0 ? (
                            <span className="rounded-full bg-white/20 px-1.5 py-px text-[10px] tabular-nums">
                              {notificationStacks.length > 99 ? '99+' : notificationStacks.length}
                            </span>
                          ) : null}
                        </button>
                      </div>
                    </div>
                    <div className="max-h-[min(70vh,26rem)] overflow-y-auto">
                      {notificationLoading ? (
                        <div className="px-4 py-5 text-center text-sm text-gray-500">Loading...</div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleEnableNotifications}
                            className="w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                              <Bell className="w-4 h-4 text-teal-600" strokeWidth={2} aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">Browser push</p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {notificationPermission === 'granted' ? 'Enabled' : notificationPermission === 'denied' ? 'Blocked in browser' : notificationPermission === 'unsupported' ? 'Not supported' : 'Tap to enable'}
                              </p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (latestAppointmentPush) {
                                handleViewNotificationDetail(latestAppointmentPush);
                                return;
                              }
                              navigate('/appointments');
                              setIsNotificationOpen(false);
                            }}
                            className="w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                              <Video className="w-4 h-4 text-cyan-700" strokeWidth={2} aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">Appointments</p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {latestAppointmentPush?.title
                                  ? `${latestAppointmentPush.title}${latestAppointmentPush?.body ? ` — ${latestAppointmentPush.body}` : ''}`
                                  : 'No appointment alerts'}
                              </p>
                            </div>
                            {appointmentPushCount > 0 && (
                              <span className="flex-shrink-0 rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[1.25rem] text-center">
                                {appointmentPushCount > 99 ? '99+' : appointmentPushCount}
                              </span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (chatPushItems[0]) {
                                handleViewNotificationDetail(chatPushItems[0]);
                                return;
                              }
                              navigate('/messages');
                              setIsNotificationOpen(false);
                            }}
                            className="w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                              <Mail className="w-4 h-4 text-teal-600" strokeWidth={2} aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">Messages</p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {unreadMessagesCount > 0
                                  ? (unreadByConversation && unreadByConversation.size > 0
                                    ? [...unreadByConversation.entries()]
                                        .map(([, e]) => (e?.displayName ? `${e.displayName} (${e?.count ?? 0})` : null))
                                        .filter(Boolean)
                                        .join(', ') || `${unreadMessagesCount} unread`
                                    : `${unreadMessagesCount} unread`)
                                  : (chatPushItems[0]?.title
                                    ? `${chatPushItems[0].title}${chatPushItems[0]?.body ? ` — ${chatPushItems[0].body}` : ''}`
                                    : 'No new messages')}
                              </p>
                            </div>
                            {(totalMessageCount > 0) && (
                              <span className="flex-shrink-0 rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[1.25rem] text-center">
                                {totalMessageCount > 99 ? '99+' : totalMessageCount}
                              </span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => { navigate('/alerts'); setIsNotificationOpen(false); }}
                            className="w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                              <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={2} aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">Alerts</p>
                              <p className="text-[11px] text-gray-500">
                                {alertsCount > 0 ? `${alertsCount} active` : 'No new alerts'}
                              </p>
                            </div>
                            {alertsCount > 0 && (
                              <span className="flex-shrink-0 rounded-full bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[1.25rem] text-center">{alertsCount > 99 ? '99+' : alertsCount}</span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => { navigate('/my-patients'); setIsNotificationOpen(false); }}
                            className="w-full text-left px-3.5 py-2.5 flex items-center gap-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                              <Heart className="w-4 h-4 text-sky-600" strokeWidth={2} aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">Patient feelings</p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {feelingsSummary.unwell > 0 || feelingsSummary.moderate > 0
                                  ? `${feelingsSummary.unwell} unwell · ${feelingsSummary.moderate} moderate`
                                  : 'Open patients'}
                              </p>
                            </div>
                          </button>

                          <div className="px-3.5 pt-2.5 pb-1 flex items-center justify-between">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                              Recent
                            </p>
                            {hasMoreNotifications ? (
                              <span className="text-[10px] font-medium text-gray-400">
                                Last {PUSH_HISTORY_PREVIEW_COUNT} threads
                              </span>
                            ) : null}
                          </div>
                          {recentStackPreview.length === 0 ? (
                            <div className="px-3.5 py-4 text-xs text-gray-500">
                              No push history yet. New alerts will stay here after refresh.
                            </div>
                          ) : (
                            <>
                              {recentStackPreview.map((stack) => (
                                <NotificationStackRow
                                  key={stack.key}
                                  stack={stack}
                                  dense
                                  onSelect={handleViewNotificationDetail}
                                />
                              ))}
                              <button
                                type="button"
                                onClick={handleOpenHistory}
                                className="w-full px-3.5 py-2.5 text-center text-[12px] font-semibold text-teal-700 hover:bg-teal-50 transition-colors border-t border-gray-100"
                              >
                                {hasMoreNotifications
                                  ? `More notifications (${notificationStacks.length})`
                                  : 'View all notifications'}
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                </DropdownPanel>
              </div>

              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 min-h-[44px]"
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-semibold text-gray-900 leading-tight">
                      {userInfo.fullName}
                    </div>
                    <div className="text-xs text-gray-500 leading-tight">
                      {userInfo.role}
                    </div>
                  </div>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" title={userInfo.fullName}>
                    {userInfo.fullName.charAt(0).toUpperCase()}
                  </div>
                </button>

                <DropdownPanel
                  open={isDropdownOpen}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden"
                >
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileOpen(true);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-inset"
                    >
                      <UserRound className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                      View Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/change-password');
                        setIsDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-2 border-t border-gray-200 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-inset"
                    >
                      <Lock className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                      Change Password
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition-colors font-medium flex items-center gap-2 border-t border-gray-200 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-inset"
                    >
                      <LogOut className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
                      Sign Out
                    </button>
                </DropdownPanel>
              </div>
            </div>
            )}
          </div>
        </div>

        {loggedIn && (
        <DropdownPanel
          open={isMobileMenuOpen}
          transformOrigin="top center"
          className="lg:hidden fixed inset-0 top-16 z-dropdown bg-white border-t border-gray-200 overflow-y-auto"
        >
          <div
            id="mobile-nav-menu"
            ref={mobileMenuRef}
            role="dialog"
            aria-label="Main navigation"
            aria-modal="true"
          >
            <nav className="flex flex-col py-4 px-4" aria-label="Main navigation">
              {NAV_LINKS.map(({ to, label, badge, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={!!end}
                  onClick={closeMobileMenu}
                  className={mobileNavLinkClass}
                >
                  {label}
                  {badge === 'messages' && totalMessageCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold text-white bg-red-500" aria-hidden="true">
                      <AnimatedNumber value={totalMessageCount > 99 ? '99+' : totalMessageCount} />
                    </span>
                  )}
                  {label === 'Appointments' && totalAppointmentCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold text-white bg-red-500" aria-hidden="true">
                      <AnimatedNumber value={totalAppointmentCount > 99 ? '99+' : totalAppointmentCount} />
                    </span>
                  )}
                  {badge === 'alerts' && alertsCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold text-white bg-red-500" aria-hidden="true">
                      <AnimatedNumber value={alertsCount > 99 ? '99+' : alertsCount} />
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        </DropdownPanel>
        )}
      </header>

      {loggedIn && (
      <>
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        user={user}
        userType={userType}
      />
      <NotificationDetailModal
        item={detailItem}
        open={!!detailItem}
        onClose={handleCloseNotificationDetail}
        onOpen={handleOpenNotificationDestination}
      />
      <NotificationsHistoryModal
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        items={webPushItems}
        onSelect={handleViewNotificationDetail}
        onMarkAllRead={markAllNotificationsRead}
      />
      </>
      )}
    </>
  );
};

export default Header;
